/**
 * Centralized RC Integration API client.
 * All call/SMS actions in the CRM route through these functions.
 *
 * IMPORTANT: every backend route MUST be prefixed with `/api` so the k8s ingress
 * rewrites the call to the FastAPI service on :8001. The canonical constant lives
 * in `src/App.js` as `export const API = \`${BACKEND_URL}/api\``; we mirror that
 * construction here to avoid an import cycle (this lib is dynamically imported
 * from components that App.js loads).
 */
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Must match App.js: every endpoint below is built as `${API}/integrations/rc/...`
// which yields `<BACKEND_URL>/api/integrations/rc/...` — the correct ingress path.
const API = `${BACKEND_URL}/api`;

function getToken() {
  return localStorage.getItem("token") || "";
}

function headers() {
  return { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" };
}

export async function getRCStatus() {
  const res = await axios.get(`${API}/integrations/rc/status`, { headers: headers() });
  return res.data;
}

/**
 * Send SMS via RC gateway.
 * @param {{ to: string, text: string, from_number?: string, lead_id?: string }} payload
 */
/**
 * Normalize an axios error into the best human-readable reason.
 * Precedence matches the CRM RC error contract:
 *   provider_reason_description > provider_reason > message > detail > fallback
 * This ensures the user sees the exact downstream provider reason when RC
 * returned a structured error (e.g. "Phone number is not SMS-enabled"), and
 * falls back gracefully for legacy/generic errors.
 */
export function pickRCErrorMessage(err, fallback) {
  const d = err?.response?.data || {};
  return (
    d.provider_reason_description ||
    d.provider_reason ||
    d.message ||
    (typeof d.detail === "string" ? d.detail : null) ||
    fallback
  );
}

export async function sendRCSms(payload) {
  try {
    const res = await axios.post(`${API}/integrations/rc/sms/send`, payload, { headers: headers() });
    toast.success("SMS sent via RingCentral");
    return res.data;
  } catch (e) {
    // Iter 178.12 — surface ownership-lock 423 with the owner name.
    if (e?.response?.status === 423 && e?.response?.data?.code === "ownership_locked") {
      const d = e.response.data;
      toast.error(d.message || `Locked — ${d.owner_name || "another rep"} owns this lead.`);
      e.isOwnershipLocked = true;
      e.lockOwnerName = d.owner_name || null;
      throw e;
    }
    toast.error(pickRCErrorMessage(e, "SMS failed"));
    throw e;
  }
}

/**
 * Initiate call via RC gateway.
 * @param {{ to: string, from_number?: string, lead_id?: string, force?: boolean }} payload
 *
 * If another rep dialed the same `lead_id` within the last 90s, the backend
 * returns 409 `rc_call_collision`. The caller receives a tagged error
 * (`isCollision: true`) so the UI can confirm with the rep and retry with
 * `force: true` to bypass the guard.
 */
export async function makeRCCall(payload) {
  try {
    const res = await axios.post(`${API}/integrations/rc/calls/make`, payload, { headers: headers() });
    toast.success("Call initiated via RingCentral");
    return res.data;
  } catch (e) {
    const data = e?.response?.data || {};
    // Iter 178.12 (Danny Feb 2026) — ownership lock from the backend.
    // The lead is owned by another rep with an active lock. We tag the
    // error so callers can offer "Request transfer" UI; default toast
    // names the owner so reps know who to coordinate with.
    if (e?.response?.status === 423 && data.code === "ownership_locked") {
      e.isOwnershipLocked = true;
      e.lockOwnerId = data.owner_id || null;
      e.lockOwnerName = data.owner_name || null;
      e.lockTier = data.tier || null;
      toast.error(data.message || `Locked — ${data.owner_name || "another rep"} owns this lead.`, {
        description: data.tier ? `Lock tier: ${data.tier}` : undefined,
      });
      throw e;
    }
    // Iter 184 (Feb 2026 — leadership ask) — 30-day DNC quiet window
    // hard-block. Surfaces the days-left so the rep knows when the
    // lead reopens. They can still email / SMS in the meantime.
    if (e?.response?.status === 423 && data.code === "dnc_active") {
      e.isDncBlocked = true;
      e.dncDaysLeft = data.days_left || null;
      e.dncReason = data.dnc_reason || null;
      toast.error(
        data.message || `Lead is marked Do Not Call — voice calls blocked.`,
        { description: data.dnc_reason ? `Reason: ${String(data.dnc_reason).replace(/_/g, " ")}` : undefined, duration: 7000 },
      );
      throw e;
    }
    if (e?.response?.status === 409 && data.code === "rc_call_collision") {
      // Annotate the error so the caller can show a "Call anyway?" confirm.
      e.isCollision = true;
      e.collisionOther = data.other_agent || null;
      toast.warning(data.message || "Another rep is already on this lead.", {
        description: data.provider_reason_description || undefined,
      });
      throw e;
    }
    toast.error(pickRCErrorMessage(e, "Call failed"));
    throw e;
  }
}

// Exported for tests/debug — lets callers assert that every URL starts with `<BACKEND_URL>/api/`.
/**
 * Fetch the live RC call log from /api/integrations/rc/call-log.
 * Returns `{items, count}` on success. Surfaces normalized RC errors via toast.
 */
export async function getRCCallLog(limit = 25) {
  try {
    const res = await axios.get(`${API}/integrations/rc/call-log?limit=${limit}`, { headers: headers() });
    return res.data;
  } catch (e) {
    // Let caller decide whether to toast; call-log polling shouldn't spam toasts.
    throw e;
  }
}

/**
 * Link an orphan RC activity row to a lead.
 * Backend: POST /api/integrations/rc/activity/{id}/link-lead
 * Returns the updated activity row (safe fields only) on success.
 */
export async function linkRCActivityToLead(activityId, leadId) {
  try {
    const res = await axios.post(
      `${API}/integrations/rc/activity/${activityId}/link-lead`,
      { lead_id: leadId },
      { headers: headers() },
    );
    toast.success("Activity linked to lead");
    return res.data;
  } catch (e) {
    toast.error(pickRCErrorMessage(e, "Failed to link activity"));
    throw e;
  }
}

export const RC_ENDPOINTS = {
  status: `${API}/integrations/rc/status`,
  smsSend: `${API}/integrations/rc/sms/send`,
  callMake: `${API}/integrations/rc/calls/make`,
  connect: `${API}/integrations/rc/connect`,
  disconnect: `${API}/integrations/rc/disconnect`,
  numbers: `${API}/integrations/rc/numbers`,
  activity: `${API}/integrations/rc/activity`,
  webhookHealth: `${API}/integrations/rc/webhook/health`,
};

export { API };
